import { completeEmbeddedSignup, getEmbeddedSignupConfig } from '../services/api';

const META_SDK_ID = 'facebook-jssdk';
let sdkPromise = null;

function isFacebookOrigin(origin) {
  return (
    origin === 'https://www.facebook.com'
    || origin === 'https://web.facebook.com'
    || origin.endsWith('.facebook.com')
  );
}

function safeJsonParse(value) {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extractEmbeddedSignupMessage(event) {
  if (!event?.origin || !isFacebookOrigin(event.origin)) {
    return null;
  }

  const payload = safeJsonParse(event.data);
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const root = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const eventName = root.event || payload.event;
  const details = root.data && typeof root.data === 'object' ? root.data : root;

  if (!eventName) {
    return null;
  }

  return {
    event: eventName,
    businessId: details.business_manager_id || details.business_id || null,
    businessName: details.business_name || null,
    wabaId: details.waba_id || null,
    phoneNumberId: details.phone_number_id || null,
    raw: payload,
  };
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function loadMetaSdk({ appId, graphApiVersion }) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Meta signup must run in a browser.'));
  }

  if (window.FB) {
    window.FB.init({
      appId,
      cookie: false,
      xfbml: false,
      version: graphApiVersion,
    });
    return Promise.resolve(window.FB);
  }

  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise((resolve, reject) => {
    const finishInit = () => {
      if (!window.FB) {
        reject(new Error('Meta SDK loaded, but FB is unavailable.'));
        return;
      }

      window.FB.init({
        appId,
        cookie: false,
        xfbml: false,
        version: graphApiVersion,
      });
      resolve(window.FB);
    };

    window.fbAsyncInit = finishInit;

    const existingScript = document.getElementById(META_SDK_ID);
    if (existingScript) {
      existingScript.addEventListener('load', finishInit, { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load the Meta SDK.')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = META_SDK_ID;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.onload = finishInit;
    script.onerror = () => reject(new Error('Failed to load the Meta SDK.'));
    document.body.appendChild(script);
  });

  return sdkPromise;
}

export async function launchWhatsAppEmbeddedSignup() {
  const { data: config } = await getEmbeddedSignupConfig();

  if (!config?.app_id || !config?.config_id) {
    throw new Error('Meta Embedded Signup is not configured yet.');
  }

  const fb = await loadMetaSdk({
    appId: config.app_id,
    graphApiVersion: config.graph_api_version || 'v18.0',
  });

  let latestMessage = null;
  let latestError = null;

  const messageHandler = (event) => {
    const parsed = extractEmbeddedSignupMessage(event);
    if (!parsed) {
      return;
    }

    latestMessage = parsed;
    if (parsed.event === 'ERROR') {
      latestError = new Error('Meta Embedded Signup returned an error.');
    }
  };

  window.addEventListener('message', messageHandler);

  try {
    const loginResponse = await new Promise((resolve) => {
      fb.login(
        (response) => resolve(response),
        {
          config_id: config.config_id,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            sessionInfoVersion: 3,
          },
        },
      );
    });

    const code = loginResponse?.authResponse?.code;
    if (!code) {
      throw latestError || new Error('Meta signup was cancelled before completion.');
    }

    // Meta may post the session identifiers a fraction later than the login callback.
    await wait(1200);

    const exchangePayload = {
      code,
      event: latestMessage?.event || null,
      business_id: latestMessage?.businessId || null,
      business_name: latestMessage?.businessName || null,
      waba_id: latestMessage?.wabaId || null,
      phone_number_id: latestMessage?.phoneNumberId || null,
      session_info: latestMessage?.raw || null,
    };

    const { data } = await completeEmbeddedSignup(exchangePayload);
    return data;
  } catch (error) {
    const message = error?.response?.data?.detail || error?.message || 'Embedded Signup failed.';
    throw new Error(message);
  } finally {
    window.removeEventListener('message', messageHandler);
  }
}
